/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type MutationFunctionContext, useMutation } from '@tanstack/react-query';
import axios, { isAxiosError } from 'axios';
import { BV_BASE_URL, BV_PASS_KEY } from '@/features/reviews/constants';
import type { InitiateSubmitResponse } from '../interface/types';
import type { SubmitReviewPageType } from '../../reviews/interface';
import { getReviewsCampaignID } from '@/features/reviews/utils/getReviewsCampaignID';
import { useNotification } from '@az/starc-ui';
import { useLabels } from '@/hooks/useLabels';
import logger from '@/utils/logger';
import { isRetryableBVError } from '../utils/retryStrategies';

const URL = `${BV_BASE_URL}/data/initiateSubmit.json`;

type InitiateSubmitOptions = {
  locale: string;
  productIds: string[];
  fp?: string;
  userToken?: string;
  pageType: SubmitReviewPageType;
  displaycode?: string;
};

type InitiateSubmitParams = {
  ApiVersion: number;
  passkey: string;
  hostedauth?: boolean;
  extended?: boolean;
};

const postInitiateSubmit = async ({
  locale,
  productIds,
  fp,
  pageType,
  userToken,
  displaycode,
}: InitiateSubmitOptions) => {
  if (productIds.length === 0) {
    throw new Error('productIds must be a non-empty array');
  }

  const params: InitiateSubmitParams = {
    ApiVersion: 5.4,
    passkey: BV_PASS_KEY,
    extended: true,
    ...(displaycode ? { displaycode } : {}),
  };

  const campaignId = getReviewsCampaignID(pageType);

  const payload = {
    locale: locale.replace('-', '_'),
    productIds,
    campaignId,
    deviceFingerprint: fp,
    userToken,
  };

  if (!userToken) {
    params.hostedauth = true;
  }

  const response = await axios.post<InitiateSubmitResponse>(URL, payload, {
    params,
  });

  if (response.data.hasErrors && response.data.errors) {
    const error = new Error(
      `Initiate submission failed: ,
      ${response.data.errors.map((error) => error.code).join(', ')}`,
    );
    Object.assign(error, {
      response: {
        data: response.data,
      },
    });
    throw error;
  }

  return {
    data: response.data,
    hasSubmitted:
      response.data.errors?.some((error) => error.code === 'ERROR_DUPLICATE_SUBMISSION') ?? false,
  };
};

type InitiateSubmitMutationProps = {
  onSuccess?: (
    data: {
      data: InitiateSubmitResponse;
      hasSubmitted: boolean;
    },
    variables: InitiateSubmitOptions,
    context: unknown,
  ) => void;
  onError?: (
    error: Error,
    variables: InitiateSubmitOptions,
    onMutateResult: unknown,
    context: MutationFunctionContext,
  ) => void;
};

const labelMap = {
  errorDuringInitializationTitle: 'label_reviews_submit_errorDuringInitializationTitle',
  errorDuringInitializationText: 'label_reviews_submit_errorDuringInitializationText',
};

export const useInitiateSubmitMutation = (props?: InitiateSubmitMutationProps) => {
  const { onSuccess, onError } = props ?? {};
  const notification = useNotification();
  const labels = useLabels(labelMap);

  return useMutation({
    mutationFn: postInitiateSubmit,
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      if (isAxiosError(error) || error instanceof Error) {
        const id = notification.show({
          type: 'error',
          title: labels.errorDuringInitializationTitle,
          text: labels.errorDuringInitializationText,
          position: 'top-end',
          hasCloseButton: true,
          onClose: () => notification.hide(id),
        });
        logger.error({
          message: 'Failed to initiate review submission',
          meta: {
            source: 'postInitiateSubmit',
            error,
          },
        });
        onError?.(error, variables, onMutateResult, context);
      }
    },
    retry: isRetryableBVError,
  });
};
